/**
 * Thin wrapper over RTCPeerConnection for one peer. Replaces simple-peer so the browser
 * bundle needs no Node polyfills (Buffer, process, streams). Signalling data is opaque JSON
 * passed through the server's rtc:signal relay.
 *
 * Negotiation follows the "perfect negotiation" pattern so either side can add a track later
 * (a desktop participant starting a screen share): the non-initiator is the polite peer and
 * rolls back on an offer collision, the initiator ignores colliding offers.
 */
export interface PeerOptions {
  initiator: boolean;
  stream: MediaStream | undefined;
  onSignal: (data: unknown) => void;
  onStream: (stream: MediaStream) => void;
  onState: (state: RTCPeerConnectionState) => void;
  iceServers?: RTCIceServer[];
}

type SignalData = { sdp: RTCSessionDescriptionInit } | { candidate: RTCIceCandidateInit | null };

const DEFAULT_ICE: RTCIceServer[] = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
];

export class Peer {
  readonly pc: RTCPeerConnection;
  private readonly pendingCandidates: RTCIceCandidateInit[] = [];
  private readonly polite: boolean;
  private makingOffer = false;
  private ignoreOffer = false;
  private closed = false;
  private readonly senders = new Map<MediaStreamTrack, RTCRtpSender>();

  constructor(private readonly options: PeerOptions) {
    this.polite = !options.initiator;
    this.pc = new RTCPeerConnection({ iceServers: options.iceServers ?? DEFAULT_ICE });
    if (options.stream)
      for (const track of options.stream.getAudioTracks()) this.addTrack(track, options.stream);
    else this.pc.addTransceiver('audio', { direction: 'recvonly' });

    this.pc.addEventListener('icecandidate', (e) => {
      options.onSignal({
        candidate: e.candidate ? e.candidate.toJSON() : null,
      } satisfies SignalData);
    });
    this.pc.addEventListener('track', (e) => {
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      options.onStream(stream);
    });
    this.pc.addEventListener('connectionstatechange', () =>
      options.onState(this.pc.connectionState),
    );
    this.pc.addEventListener('negotiationneeded', () => void this.makeOffer());
  }

  private async makeOffer(): Promise<void> {
    if (this.closed) return;
    try {
      this.makingOffer = true;
      await this.pc.setLocalDescription();
      if (this.pc.localDescription)
        this.options.onSignal({ sdp: this.pc.localDescription.toJSON() } satisfies SignalData);
    } catch (err) {
      console.warn('[voice] offer failed', err);
    } finally {
      this.makingOffer = false;
    }
  }

  /** Feed a signal from the remote side. */
  async signal(data: unknown): Promise<void> {
    if (this.closed || typeof data !== 'object' || data === null) return;
    const d = data as Partial<SignalData>;
    try {
      if ('sdp' in d && d.sdp) {
        const offerCollision =
          d.sdp.type === 'offer' && (this.makingOffer || this.pc.signalingState !== 'stable');
        this.ignoreOffer = !this.polite && offerCollision;
        if (this.ignoreOffer) return;
        await this.pc.setRemoteDescription(d.sdp);
        if (d.sdp.type === 'offer') {
          await this.pc.setLocalDescription();
          if (this.pc.localDescription)
            this.options.onSignal({ sdp: this.pc.localDescription.toJSON() } satisfies SignalData);
        }
        for (const c of this.pendingCandidates.splice(0)) await this.pc.addIceCandidate(c);
      } else if ('candidate' in d) {
        if (!this.pc.remoteDescription) {
          if (d.candidate) this.pendingCandidates.push(d.candidate);
          return;
        }
        try {
          if (d.candidate) await this.pc.addIceCandidate(d.candidate);
        } catch (err) {
          if (!this.ignoreOffer) throw err;
        }
      }
    } catch (err) {
      console.warn('[voice] signal failed', err);
    }
  }

  /** Replace the outgoing microphone track (device changes); mute keeps the track. */
  replaceTrack(track: MediaStreamTrack | null): void {
    for (const sender of this.pc.getSenders())
      if ((sender.track?.kind === 'audio' || !sender.track) && !this.isShareSender(sender))
        void sender.replaceTrack(track);
  }

  /** Adds a track (a screen share's video or audio); renegotiates automatically. */
  addTrack(track: MediaStreamTrack, stream: MediaStream): void {
    if (this.closed || this.senders.has(track)) return;
    this.senders.set(track, this.pc.addTrack(track, stream));
  }

  removeTrack(track: MediaStreamTrack): void {
    const sender = this.senders.get(track);
    if (!sender || this.closed) return;
    this.senders.delete(track);
    try {
      this.pc.removeTrack(sender);
    } catch {
      /* already closed */
    }
  }

  private isShareSender(sender: RTCRtpSender): boolean {
    for (const [track, s] of this.senders) if (s === sender && track.kind === 'video') return true;
    return false;
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    this.pc.close();
  }
}
